import { useState, useEffect } from 'react';
import { Activity, Users, BookOpen, Brain, BarChart3 } from 'lucide-react';
import { supabase } from '../../services/supabase';
import { logger } from '../../services/logger';

interface MetricCardProps {
  title: string;
  value: string;
  change: string;
  icon: React.ElementType;
  trend?: 'up' | 'down';
}

interface AnalyticsData {
  activeUsers: number;
  journalEntriesCount: number;
  coachMessagesCount: number;
  analyticsEventsCount: number;
  activeUsersTrend: number;
  journalEntriesTrend: number;
  coachMessagesTrend: number;
  eventsTrend: number;
  dailyActivity: Array<{ date: string; count: number }>;
}

export function AnalyticsPage() {
  const [dateRange, setDateRange] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    activeUsers: 0,
    journalEntriesCount: 0,
    coachMessagesCount: 0,
    analyticsEventsCount: 0,
    activeUsersTrend: 0,
    journalEntriesTrend: 0,
    coachMessagesTrend: 0,
    eventsTrend: 0,
    dailyActivity: []
  });

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  async function fetchAnalytics() {
    try {
      setLoading(true);
      setError(null);

      const days = parseInt(dateRange);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const prevStartDate = new Date();
      prevStartDate.setDate(prevStartDate.getDate() - (days * 2));
      const prevEndDate = new Date();
      prevEndDate.setDate(prevEndDate.getDate() - days);

      const { count: activeUsers } = await supabase
        .from('analytics_events')
        .select('user_id', { count: 'exact', head: true })
        .gte('timestamp', startDate.toISOString());

      const { count: prevActiveUsers } = await supabase
        .from('analytics_events')
        .select('user_id', { count: 'exact', head: true })
        .gte('timestamp', prevStartDate.toISOString())
        .lt('timestamp', prevEndDate.toISOString());

      const { count: journalEntriesCount } = await supabase
        .from('journal_entries')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startDate.toISOString());

      const { count: prevJournalEntriesCount } = await supabase
        .from('journal_entries')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', prevStartDate.toISOString())
        .lt('created_at', prevEndDate.toISOString());

      const { count: coachMessagesCount } = await supabase
        .from('coach_messages')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startDate.toISOString());

      const { count: prevCoachMessagesCount } = await supabase
        .from('coach_messages')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', prevStartDate.toISOString())
        .lt('created_at', prevEndDate.toISOString());

      const { count: analyticsEventsCount } = await supabase
        .from('analytics_events')
        .select('*', { count: 'exact', head: true })
        .gte('timestamp', startDate.toISOString());

      const { count: prevAnalyticsEventsCount } = await supabase
        .from('analytics_events')
        .select('*', { count: 'exact', head: true })
        .gte('timestamp', prevStartDate.toISOString())
        .lt('timestamp', prevEndDate.toISOString());

      const { data: dailyEvents } = await supabase
        .from('analytics_events')
        .select('timestamp')
        .gte('timestamp', startDate.toISOString())
        .order('timestamp', { ascending: true });

      const dailyActivity = processDailyActivity(dailyEvents || [], days);

      const calculateTrend = (current: number, previous: number) => {
        if (previous === 0) return current > 0 ? 100 : 0;
        return Math.round(((current - previous) / previous) * 100);
      };

      setAnalytics({
        activeUsers: activeUsers || 0,
        journalEntriesCount: journalEntriesCount || 0,
        coachMessagesCount: coachMessagesCount || 0,
        analyticsEventsCount: analyticsEventsCount || 0,
        activeUsersTrend: calculateTrend(activeUsers || 0, prevActiveUsers || 0),
        journalEntriesTrend: calculateTrend(journalEntriesCount || 0, prevJournalEntriesCount || 0),
        coachMessagesTrend: calculateTrend(coachMessagesCount || 0, prevCoachMessagesCount || 0),
        eventsTrend: calculateTrend(analyticsEventsCount || 0, prevAnalyticsEventsCount || 0),
        dailyActivity
      });

      setLoading(false);
    } catch (err: any) {
      logger.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
      setLoading(false);
    }
  }

  function processDailyActivity(events: any[], days: number) {
    const dailyCounts: { [key: string]: number } = {};

    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() - (days - 1 - i));
      const dateKey = date.toISOString().split('T')[0];
      dailyCounts[dateKey] = 0;
    }

    events.forEach(event => {
      const dateKey = event.timestamp.split('T')[0];
      if (dailyCounts[dateKey] !== undefined) {
        dailyCounts[dateKey]++;
      }
    });

    return Object.entries(dailyCounts).map(([date, count]) => ({
      date,
      count
    }));
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
          {error}
        </div>
        <button
          onClick={() => fetchAnalytics()}
          className="px-4 py-2 bg-gold-500 text-ink-950 rounded-lg hover:bg-gold-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const maxActivity = Math.max(...analytics.dailyActivity.map(d => d.count), 1);

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-ink-950">Analytics</h1>
          <p className="text-gray-600">Track user engagement and platform metrics</p>
        </div>
        <div className="flex gap-2">
          {['7d', '30d', '90d'].map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                dateRange === range
                  ? 'bg-gold-500 text-ink-950'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              Last {range}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Active Users"
            value={analytics.activeUsers.toLocaleString()}
            change={`${analytics.activeUsersTrend > 0 ? '+' : ''}${analytics.activeUsersTrend}%`}
            icon={Users}
            trend={analytics.activeUsersTrend >= 0 ? 'up' : 'down'}
          />
          <MetricCard
            title="Journal Entries"
            value={analytics.journalEntriesCount.toLocaleString()}
            change={`${analytics.journalEntriesTrend > 0 ? '+' : ''}${analytics.journalEntriesTrend}%`}
            icon={BookOpen}
            trend={analytics.journalEntriesTrend >= 0 ? 'up' : 'down'}
          />
          <MetricCard
            title="Coach Messages"
            value={analytics.coachMessagesCount.toLocaleString()}
            change={`${analytics.coachMessagesTrend > 0 ? '+' : ''}${analytics.coachMessagesTrend}%`}
            icon={Brain}
            trend={analytics.coachMessagesTrend >= 0 ? 'up' : 'down'}
          />
          <MetricCard
            title="Total Events"
            value={analytics.analyticsEventsCount.toLocaleString()}
            change={`${analytics.eventsTrend > 0 ? '+' : ''}${analytics.eventsTrend}%`}
            icon={Activity}
            trend={analytics.eventsTrend >= 0 ? 'up' : 'down'}
          />
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-ink-950">User Activity Over Time</h2>
            <BarChart3 className="h-5 w-5 text-gray-400" />
          </div>
          <div className="h-64 flex items-end justify-between gap-2">
            {analytics.dailyActivity.map((day, i) => {
              const height = (day.count / maxActivity) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center group">
                  <div
                    className="w-full bg-gold-500/20 hover:bg-gold-500/40 rounded-t transition-all cursor-pointer relative"
                    style={{ height: `${height}%`, minHeight: '4px' }}
                    title={`${new Date(day.date).toLocaleDateString()}: ${day.count} events`}
                  >
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-ink-950 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {day.count} events
                    </div>
                  </div>
                  {i % Math.ceil(analytics.dailyActivity.length / 7) === 0 && (
                    <span className="text-xs text-gray-500 mt-2">
                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <h2 className="text-lg font-semibold text-ink-950 mb-4">Engagement Breakdown</h2>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Journal Entries</span>
                  <span className="font-medium text-ink-950">{analytics.journalEntriesCount}</span>
                </div>
                <div className="h-2 bg-paper rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gold-500 rounded-full transition-all"
                    style={{
                      width: `${Math.min((analytics.journalEntriesCount / (analytics.analyticsEventsCount || 1)) * 100, 100)}%`
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Coach Messages</span>
                  <span className="font-medium text-ink-950">{analytics.coachMessagesCount}</span>
                </div>
                <div className="h-2 bg-paper rounded-full overflow-hidden">
                  <div
                    className="h-full bg-electric-400 rounded-full transition-all"
                    style={{
                      width: `${Math.min((analytics.coachMessagesCount / (analytics.analyticsEventsCount || 1)) * 100, 100)}%`
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <h2 className="text-lg font-semibold text-ink-950 mb-4">Quick Stats</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-paper rounded-lg">
                <span className="text-sm text-gray-600">Avg. Events/Day</span>
                <span className="text-lg font-bold text-ink-950">
                  {Math.round(analytics.analyticsEventsCount / parseInt(dateRange))}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-paper rounded-lg">
                <span className="text-sm text-gray-600">Avg. Journals/Day</span>
                <span className="text-lg font-bold text-ink-950">
                  {Math.round(analytics.journalEntriesCount / parseInt(dateRange))}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-paper rounded-lg">
                <span className="text-sm text-gray-600">Avg. Coach/Day</span>
                <span className="text-lg font-bold text-ink-950">
                  {Math.round(analytics.coachMessagesCount / parseInt(dateRange))}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value, change, icon: Icon, trend }: MetricCardProps) {
  return (
    <div className="bg-white p-6 rounded-xl border border-gray-200 hover:border-gold-500/30 transition-colors">
      <div className="flex items-center justify-between mb-4">
        <Icon className="h-6 w-6 text-gold-500" />
        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
          trend === 'up' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {change}
        </span>
      </div>
      <p className="text-sm text-gray-600 mb-1">{title}</p>
      <p className="text-2xl font-bold text-ink-950">{value}</p>
    </div>
  );
}
